/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { TextAlign, GridComponent } from '@syncfusion/ej2-react-grids'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { usePageContentGridScrollMaxHeight } from '@hooks/usePageContentGridScrollMaxHeight'
import StatusBadge from '@components/common/StatusBadge/StatusBadge.component'
import ClaimPostingInfo from '@components/layout/Modals/ClaimPostingInfo/ClaimPostingInfo.component'
import { getTrackingCheckStatus } from '@utils/tracking.util'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { deleteTrackingTransaction } from '@services/api/tracking.api'
import { EmptyState } from '@components/ui'

import { logger } from '@utils/safeLogger'

type Props = {
  trackingTransactions: any[]
  filters: {
    modeOfPayment: string[]
    insuranceCarrier: string[]
    checkStatus: string[]
  }
  searchQuery: string
  onPostSuccess: () => void
  onDeleteSuccess: () => void
  onAddDeposit?: () => void
}

const TrackingManagement: React.FC<Props> = ({
  trackingTransactions,
  filters,
  searchQuery,
  onPostSuccess,
  onDeleteSuccess,
  onAddDeposit,
}) => {
  const gridRef = useRef<GridComponent | null>(null)
  const trackingGridSectionRef = useRef<HTMLDivElement>(null)

  const [isClaimPostingModalVisible, setClaimPostingModalVisible] =
    useState<boolean>(false)
  const [, setSelectedCheckStatus] = useState<string | null>(null)
  const [selectedReferenceNumber, setSelectedReferenceNumber] = useState<
    string | null
  >(null)
  const [selectedTrackingTransaction, setSelectedTrackingTransaction] =
    useState<any | null>(null)
  const [selectedCheckNumber, setSelectedCheckNumber] = useState<string | null>(
    null,
  )
  const [transactionToDelete, setTransactionToDelete] = useState<any | null>(
    null,
  )
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] =
    useState<boolean>(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const handleRowClick = (rowData: any): void => {
    setSelectedCheckStatus(rowData.checkStatus)
    setSelectedCheckNumber(rowData.checkEFTNumber)
    setSelectedReferenceNumber(rowData.reference_number)
    setSelectedTrackingTransaction(rowData)
    setClaimPostingModalVisible(true)
  }

  const onDeleteTransaction = (transaction: any): void => {
    setTransactionToDelete(transaction)
    setShowConfirmDeleteModal(true)
  }

  const columnsTracking = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '10px',
      },
      {
        field: 'payment_method',
        headerText: 'Mop',
        width: '160px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'reference_number',
        headerText: 'Check/EFT/Ref',
        width: '200px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'payer',
        headerText: 'Payer',
        width: 'auto',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'amount',
        headerText: 'Amount ($)',
        width: '160px',
        textAlign: 'Left' as TextAlign,
        template: (rowData: any) => {
          return <span>{Math.abs(parseFloat(rowData.amount)).toFixed(2)}</span>
        },
      },
      {
        field: 'authorized_date',
        headerText: 'Issue date',
        width: '120px',
        textAlign: 'Left' as TextAlign,
        template: (rowData: any) => {
          return (
            <span>
              {rowData.authorized_date
                ? new Date(rowData.authorized_date).toISOString().slice(0, 10)
                : ''}
            </span>
          )
        },
      },
      {
        field: 'date',
        headerText: 'Received on',
        width: '120px',
        textAlign: 'Left' as TextAlign,
        template: (rowData: any) => {
          return (
            <span>
              {rowData.date
                ? new Date(rowData.date).toISOString().slice(0, 10)
                : ''}
            </span>
          )
        },
      },
      {
        field: 'postedDate',
        headerText: 'Posted date',
        width: '120px',
        textAlign: 'Left' as TextAlign,
        template: (rowData: any) => {
          return (
            <span>
              {rowData.posted_date
                ? new Date(rowData.posted_date).toISOString().slice(0, 10)
                : ''}
            </span>
          )
        },
      },
      {
        field: 'no_of_claims',
        headerText: 'No. of claims',
        width: '120px',
        textAlign: 'Left' as TextAlign,
        template: (rowData: any) => {
          return (
            <span>
              {rowData.no_of_claims === 0 ? '-' : rowData.no_of_claims}
            </span>
          )
        },
      },
      {
        field: 'checkStatus',
        headerText: 'Status',
        width: '120px',
        textAlign: 'Left' as TextAlign,
        template: (rowData: any) => {
          const status = getTrackingCheckStatus(rowData)
          return <StatusBadge status={status} type="tracking" />
        },
      },
      {
        field: 'actions',
        headerText: 'Actions',
        width: '120px',
        textAlign: 'Center' as TextAlign,
        template: (rowData: any) => {
          const status = getTrackingCheckStatus(rowData)
          return (
            <ActionButtons
              {...(status === 'Pending'
                ? {
                    onDelete: () => onDeleteTransaction(rowData),
                  }
                : {})}
              {...(status === 'Processing' ||
              status === 'Deposited' ||
              status === 'Posted'
                ? { onView: () => handleRowClick(rowData) }
                : {})}
              showView={true}
              showMoreOptions={false}
            />
          )
        },
      },
    ]
  }, [])

  const filteredTrackingTransactions = trackingTransactions.filter(
    (transaction) => {
      const {
        modeOfPayment = [],
        insuranceCarrier = [],
        checkStatus = [],
      } = filters
      const matchesSearchQuery = [
        transaction.payment_method,
        transaction.reference_number,
        transaction.payer,
        transaction.amount,
        transaction.authorized_date
          ? new Date(transaction.authorized_date).toISOString().slice(0, 10)
          : '',
        transaction.date
          ? new Date(transaction.date).toISOString().slice(0, 10)
          : '',
        transaction.posted_date
          ? new Date(transaction.posted_date).toISOString().slice(0, 10)
          : '',
        getTrackingCheckStatus(transaction) as string,
      ].some((field) =>
        field
          ?.toString()
          .toLowerCase()
          .includes(searchQuery?.toLowerCase() || ''),
      )
      const matchesModeOfPayment =
        modeOfPayment.length === 0 ||
        modeOfPayment.includes(transaction.payment_method)
      const matchesInsuranceCarrier =
        insuranceCarrier.length === 0 ||
        insuranceCarrier.includes(transaction.payer)
      const matchesCheckStatus =
        checkStatus.length === 0 ||
        checkStatus.includes(getTrackingCheckStatus(transaction))

      return (
        matchesSearchQuery &&
        matchesModeOfPayment &&
        matchesInsuranceCarrier &&
        matchesCheckStatus
      )
    },
  )

  const paymentStats = useMemo(() => {
    return {
      total: filteredTrackingTransactions.reduce(
        (acc, record) => acc + Math.abs(Number(record.amount || 0)),
        0,
      ),
      posted: filteredTrackingTransactions.reduce(
        (acc, record) =>
          getTrackingCheckStatus(record) === 'Posted'
            ? acc + Math.abs(Number(record.amount || 0))
            : acc,
        0,
      ),
      deposited: filteredTrackingTransactions.reduce(
        (acc, record) =>
          getTrackingCheckStatus(record) === 'Deposited'
            ? acc + Math.abs(Number(record.amount || 0))
            : acc,
        0,
      ),
      processing: filteredTrackingTransactions.reduce(
        (acc, record) =>
          getTrackingCheckStatus(record) === 'Processing'
            ? acc + Math.abs(Number(record.amount || 0))
            : acc,
        0,
      ),
      pending: filteredTrackingTransactions.reduce(
        (acc, record) =>
          getTrackingCheckStatus(record) === 'Pending'
            ? acc + Math.abs(Number(record.amount || 0))
            : acc,
        0,
      ),
    }
  }, [filteredTrackingTransactions])

  const trackingGridScrollStyle = usePageContentGridScrollMaxHeight(
    trackingGridSectionRef,
    [filteredTrackingTransactions.length, searchQuery, filters],
    { disabled: filteredTrackingTransactions.length === 0 },
  )

  const handleDeleteTransaction = useCallback(async (): Promise<void> => {
    if (transactionToDelete) {
      showLoading({
        container: '.tracking-transaction-delete-modal',
      })
      try {
        const response = await deleteTrackingTransaction(
          transactionToDelete.tracking_transaction_id,
        )
        if (
          response.statusCode === 200 &&
          response?.data?.status === 'Success'
        ) {
          setTimeout(() => {
            hideLoading('.tracking-transaction-delete-modal')
            showToast(
              createSuccessToast(
                'Manual transaction deleted successfully',
                'Manual transaction has been deleted successfully.',
              ),
            )
            onDeleteSuccess()
          }, 1000)
        } else {
          setTimeout(() => {
            hideLoading('.tracking-transaction-delete-modal')
            showToast(
              createFailedToast(
                'Manual transaction not deleted',
                'Manual transaction not deleted. Please try again.',
              ),
            )
          }, 1000)
        }
      } catch (e) {
        logger.error('Error deleting manual transaction:', e)
        setTimeout(() => {
          hideLoading('.tracking-transaction-delete-modal')
          showToast(
            createFailedToast(
              'Manual transaction not deleted',
              'Manual transaction not deleted. Please try again.',
            ),
          )
        }, 1000)
      } finally {
        setShowConfirmDeleteModal(false)
      }
    }
  }, [
    transactionToDelete,
    showLoading,
    hideLoading,
    showToast,
    onDeleteSuccess,
  ])

  return (
    <div className="section-tracking-management">
      <Row className="section-tracking-management-outer">
        <Col md={12} className="mc-tracking-main">
          <div className="cc-metrics-cards">
            {Object.entries(paymentStats).map(([key, metric]) => (
              <div key={key} className="cc-metrics-card">
                <span className="cc-mc-title">
                  {key.charAt(0).toUpperCase() + key.slice(1)} payments{' '}
                  <span
                    className="mgc_information_fill info-icon"
                    data-bs-toggle="tooltip"
                    title={`${key.charAt(0).toUpperCase() + key.slice(1)} payments`}
                  />
                </span>
                <div className="cc-mc-value">
                  <span>${Math.abs(parseFloat(metric)).toFixed(2)}</span>
                </div>
                <div className="cc-mc-date">
                  <span className="cc-mc-date-number">
                    As of{' '}
                    {new Date().toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={trackingGridSectionRef}
            className="grid-container page-grid-scroll-section"
            style={trackingGridScrollStyle}
          >
            {filteredTrackingTransactions.length === 0 ? (
              <EmptyState
                className="claims-empty"
                iconClass="mgc mgc_receive_money_fill"
                title="No transactions found"
                description={
                  trackingTransactions.length === 0
                    ? "You don't have any transactions on record yet."
                    : 'No transactions match your search.'
                }
                actionLabel={
                  trackingTransactions.length === 0 ? 'Add deposit' : undefined
                }
                onAction={
                  trackingTransactions.length === 0 ? onAddDeposit : undefined
                }
              />
            ) : (
              <DataGridComponent
                ref={gridRef}
                data={filteredTrackingTransactions ?? []}
                columns={columnsTracking}
                allowPaging={true}
                allowSorting={true}
                rowHeight={36}
                selectionSettings={{
                  type: 'Single',
                  mode: 'Row',
                  enableToggle: false,
                }}
                customCSS="tracking-data-grid"
              />
            )}
          </div>
        </Col>
      </Row>

      {isClaimPostingModalVisible && (
        <ClaimPostingInfo
          isVisible={isClaimPostingModalVisible}
          closeModal={() => setClaimPostingModalVisible(false)}
          referenceNumber={selectedReferenceNumber!}
          trackingTransactionData={selectedTrackingTransaction!}
          onPostSuccess={onPostSuccess}
        />
      )}

      {showConfirmDeleteModal && (
        <ConfirmDeleteModal
          show={showConfirmDeleteModal}
          onHide={() => setShowConfirmDeleteModal(false)}
          onConfirm={handleDeleteTransaction}
          entityName={
            transactionToDelete ? transactionToDelete.reference_number : ''
          }
          title="Confirm deletion"
          message={`Are you sure you want to delete this manual transaction with check/EFT number `}
          confirmButtonText="Delete"
        />
      )}
    </div>
  )
}

export default TrackingManagement
